import { Simulate } from 'react-dom/test-utils';
import { DataHooks } from './constants';
import { deprecationTestkitLog } from '../../utils/deprecationTestkitLog';

export type NavButtonName = 'first' | 'previous' | 'next' | 'last';

export const paginationCoreDriverFactory = ({
  element,
  eventTrigger,
}: {
  element: Element | undefined;
  eventTrigger: typeof Simulate;
}) => {
  deprecationTestkitLog(
    'paginationCoreDriverFactory legacy testkit is deprecated and will be removed in next major release. Please use paginationCoreUniDriverFactory from PaginationCore.uni.driver instead.',
  );

  const pageStrip: Element | null | undefined = element?.querySelector(
    '[data-hook="page-strip"]',
  );

  const getNavButton = (name: NavButtonName): Element | null | undefined =>
    element?.querySelector(`[data-hook=${name}]`);

  const getPageByNumber = (n: number): Element | null | undefined =>
    pageStrip?.firstElementChild?.querySelector(`[data-hook~=page-${n}]`);

  const getElement = (dataHook: string) =>
    element?.querySelector(`[data-hook="${dataHook}"]`);

  const getPageElements = () =>
    pageStrip?.firstElementChild?.children
      ? Array.from(pageStrip?.firstElementChild?.children)
      : [];

  const inputElement = element?.querySelector(
    '[data-hook="page-input"]',
  ) as HTMLInputElement;

  return {
    /** Returns the root element*/
    root: element,
    /** Returns whether the element exists */
    exists: (): boolean => !!element,
    /** Simulates events */
    simulate: eventTrigger,
    /** Returns the container of page elements */
    getPageStrip: () => pageStrip,
    /** Returns displayed page elements */
    getPages: getPageElements,
    /** Returns the text content of the displayed pages in "pages" mode */
    getPageLabels: () => getPageElements().map(p => p.textContent),
    /** Returns the page element given its index in the page strip */
    getPageByIndex: (idx?: number): Element | null =>
      getPageElements()[idx || 0] || null,
    /** Returns the page element given page number */
    getPageByNumber,
    /** Returns the page element currently selected */
    getCurrentPage: (): Element | null | undefined =>
      element?.querySelector('[data-hook~="current-page"]'),
    /** Returns the element for the navigation button - acceptable values are 'first', 'last', 'previous' or 'next' */
    getNavButton,
    /** Returns the page input element in "input" mode */
    getPageInput: inputElement,
    /** Returns the total amount of pages displayed in "input" mode */
    getTotalPagesField: (): Element | null | undefined =>
      element?.querySelector('[data-hook="total-pages"]'),
    /** Simulates clicking a nav button */
    clickNavButton: (name: NavButtonName): void => {
      const navButton = getNavButton(name);
      if (navButton) {
        eventTrigger.click(navButton);
      }
    },
    /** Simulates clicking a page in "pages" mode */
    clickPage: (page: number): void => {
      const pageButton = getPageByNumber(page);
      if (pageButton) {
        eventTrigger.click(pageButton);
      }
    },
    click: () => element && eventTrigger.click(element),
    /** Simulates changing the value of the input field in "input" mode */
    changeInput: (newValue: string): void => {
      if (inputElement) {
        inputElement.value = newValue;
        eventTrigger.change(inputElement);
      }
    },
    /** Simulates committing the input field value in "input" mode */
    commitInput: (): void =>
      eventTrigger.keyDown(inputElement, { keyCode: 13, key: 'Enter' }),
    /** Simulates blur in the input field in "input" mode */
    blurInput: (): void => eventTrigger.blur(inputElement),

    getElement,
    getPage: (pageNumber: number) =>
      element?.querySelector(`[data-hook~="${DataHooks.page}-${pageNumber}"]`),
    focusInput: (): void => eventTrigger.focus(inputElement),

    getElementAttribute: (dataHook: string, attributeName: string) =>
      getElement(dataHook)?.getAttribute(attributeName),
  };
};
